import { useEffect, useState } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Grid from "@mui/material/Grid"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import { RULES, validateFields, hasErrors } from "../../fieldRules"

const emptyForm = {
  code: "",
  name: "",
  assetClassCode: "",
  sunPlAccount: "",
  sunBsAccount: "",
  isActive: true,
  // Barrier-removal flags (2026-09) — false lets this category be picked in
  // a "create PO/Payment without Order/PO" (directOrder) form. Default true
  // on create, matching the backend's own default.
  mustHaveOrder: true,
  mustHavePurchase: true,
}

// Create/edit modal for a pro.budget_category row — the only place category
// admin data (code/name/SUN accounts/the 2 barrier-removal flags) is edited
// from the FE. Mirrors the inline "+ Create new category" fields in
// AddBudgetLineModal.js, plus the 2 new flags and (unlike that inline form)
// works for edit too via useUpdateBudgetCategoryMutation.
const CategoryFormModal = ({ open, onClose, category, busy = false, onSave }) => {
  const isEdit = !!category
  const [form, setForm] = useState(emptyForm)
  const [errors, setErrors] = useState({})
  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }))
    setErrors((p) => ({ ...p, [k]: null }))
  }

  useEffect(() => {
    if (!open) return
    setErrors({})
    setForm(
      category
        ? {
            code: category.code || "",
            name: category.name || "",
            assetClassCode: category.assetClassCode || "",
            sunPlAccount: category.sunPlAccount || "",
            sunBsAccount: category.sunBsAccount || "",
            isActive: category.isActive ?? true,
            mustHaveOrder: category.mustHaveOrder ?? true,
            mustHavePurchase: category.mustHavePurchase ?? true,
          }
        : emptyForm
    )
  }, [open, category])

  const isValid = form.name.trim().length > 0

  const handleSubmit = () => {
    if (!isValid) return
    const errs = validateFields(form, RULES.budgetCategory)
    setErrors(errs)
    if (hasErrors(errs)) return
    onSave({
      code: form.code.trim() || undefined,
      name: form.name.trim(),
      assetClassCode: form.assetClassCode.trim() || undefined,
      sunPlAccount: form.sunPlAccount.trim() || undefined,
      sunBsAccount: form.sunBsAccount.trim() || undefined,
      isActive: !!form.isActive,
      mustHaveOrder: !!form.mustHaveOrder,
      mustHavePurchase: !!form.mustHavePurchase,
    })
  }

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Edit category" : "New category"}</DialogTitle>
      <DialogContent>
        <MDBox pt={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                labelType="non-sticky"
                labelText="Code"
                value={form.code}
                error={!!errors.code}
                helperText={errors.code}
                onChange={(e) => setField("code", e.target.value)}
                disabled={busy}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <MDInput
                fullWidth
                required
                labelType="non-sticky"
                labelText="Name"
                value={form.name}
                error={!!errors.name}
                helperText={errors.name}
                onChange={(e) => setField("name", e.target.value)}
                disabled={busy}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                labelType="non-sticky"
                labelText="Asset class code"
                value={form.assetClassCode}
                error={!!errors.assetClassCode}
                helperText={errors.assetClassCode}
                onChange={(e) => setField("assetClassCode", e.target.value)}
                disabled={busy}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                labelType="non-sticky"
                labelText="SUN P&L account"
                value={form.sunPlAccount}
                error={!!errors.sunPlAccount}
                helperText={errors.sunPlAccount}
                onChange={(e) => setField("sunPlAccount", e.target.value)}
                disabled={busy}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                labelType="non-sticky"
                labelText="SUN BS account"
                value={form.sunBsAccount}
                error={!!errors.sunBsAccount}
                helperText={errors.sunBsAccount}
                onChange={(e) => setField("sunBsAccount", e.target.value)}
                disabled={busy}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={form.isActive} onChange={(e) => setField("isActive", e.target.checked)} disabled={busy} />}
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!form.mustHaveOrder}
                    onChange={(e) => setField("mustHaveOrder", !e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Allow PO without Order"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!form.mustHavePurchase}
                    onChange={(e) => setField("mustHavePurchase", !e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Allow Payment without PO"
              />
            </Grid>
          </Grid>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="dark" onClick={onClose} disabled={busy}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color="info" onClick={handleSubmit} disabled={busy || !isValid}>
          {busy ? "Saving…" : "Save"}
        </MDButton>
      </DialogActions>
    </Dialog>
  )
}

export default CategoryFormModal
